import { Link } from "react-router-dom";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import ServiceSchema from "../ServiceSchema";
import HeroSection from "./HeroSection";          // Same folder (as requested)
import industrialShed from "../assets/industrial-structure.jpg"; // Adjust path if needed

const IndustrialSheds = () => {
  const applications = [
    "Manufacturing Units",
    "Warehouses & Godowns",
    "Workshops",
    "Fabrication Plants",
    "Machinery Storage",
    "Automobile & Service Units",
    "Chemical / Engineering Units",
  ];

  const features = [
    {
      title: "Heavy-Duty Structural Strength",
      description: "Built to handle industrial loads without deformation.",
    },
    {
      title: "Weather & Corrosion Resistant",
      description: "High-grade steel & protective coatings extend shed lifespan.",
    },
    {
      title: "Custom Design Options",
      description: "Choose your height, width, roofing type, ventilation, and layout.",
    },
    {
      title: "Quick Installation",
      description: "Designed for fast fabrication and easy on-site erection.",
    },
    {
      title: "Affordable & Long-Lasting",
      description: "Cost-effective compared to civil construction with reduced maintenance needs.",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Buy High-Quality Industrial Sheds in Hyderabad | TR Structures"
        description="Buy durable, high-quality industrial sheds and PEB industrial sheds from TR Structures, a trusted industrial shed manufacturer in Hyderabad offering strong steel construction and custom designs."
        keywords="industrial sheds, industrial sheds manufacturers, PEB industrial sheds, industrial sheds Hyderabad, industrial shed manufacturer Hyderabad, steel industrial sheds, custom industrial sheds, industrial shed near me, industrial shed cost per sq ft, industrial shed contractor Hyderabad, industrial shed builders Telangana"
      />
      <ServiceSchema
        serviceType="Industrial Shed Design & Construction"
        description="Design, fabrication and erection of heavy-duty industrial sheds for manufacturing, warehousing, workshops and storage."
      />

      <main>
        <HeroSection
          title="Industrial Sheds"
          subtitle="Strong, Durable & Efficient Structures"
          description="Heavy-strength industrial sheds that support heavy machinery, storage, manufacturing processes, and round-the-clock activity."
          backgroundImage={industrialShed}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* Main Content */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left order-2 lg:order-1">
                <h2 className="font-montserrat text-3xl sm:text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Engineered for <span className="text-secondary">Industrial Excellence</span>
                </h2>
                <p className="text-muted-foreground mb-4 text-3xl md:text-base">
                  Our industrial sheds are constructed using premium-grade Mild Steel (MS), Stainless Steel (SS), and Galvanized materials, ensuring maximum durability and weather resistance.
                </p>
                <p className="text-muted-foreground mb-6 text-2xl md:text-base">
                  Built to withstand heat, rain, wind, and industrial wear, these sheds provide a safe and stable environment for your daily operations. We design sheds based on accurate load calculations, ventilation needs, and workflow planning.
                </p>

                <h3 className="font-montserrat text-2xl sm:text-xl font-bold text-foreground mb-4">
                  Applications:
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {applications.map((app) => (
                    <div key={app} className="flex items-center gap-2 text-muted-foreground text-2xl md:text-base">
                      <svg
                        className="w-5 h-5 text-secondary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {app}
                    </div>
                  ))}
                </div>
              </div>

              <div className="animate-slide-in-right order-1 lg:order-2">
                <img
                  src={industrialShed}
                  alt="Industrial Shed"
                  className="rounded-xl shadow-2xl w-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-montserrat text-3xl sm:text-3xl md:text-4xl font-bold text-foreground mb-4">
                Why Choose Our <span className="text-primary">Industrial Sheds?</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`p-6 bg-card rounded-xl shadow-lg card-hover animate-slide-up stagger-${index + 1}`}
                  style={{ animationFillMode: "both" }}
                >
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-secondary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="font-montserrat text-2xl sm:text-xl font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-2xl md:text-base">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-primary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-3xl sm:text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Built for Today. Ready for Tomorrow.
            </h2>
            <p className="text-primary-foreground/90 text-2xl sm:text-lg mb-8 max-w-2xl mx-auto">
              Whether you need a small production shed or a large industrial facility, TR Structures provides strong, reliable, and cost-effective solutions.
            </p>
            <Link to="/enquiry" className="btn-secondary inline-block px-8 py-4 text-2xl sm:text-lg font-medium">
              Get Your Custom Quote
            </Link>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default IndustrialSheds;